import { MARKUP_KEY_BY_MATRIX, MATRIX_LABEL_BY_KEY, SOURCE_TYPE_BY_MATRIX } from '@crm/shared'
import type { RequestItem, SourceType } from '../../../shared/api/types'
import type { SelectedPrice } from '../hooks/useKpMode'
import type { MarkupConstants } from '../../../shared/api/exchangeRates.api'
import { convertQuoteToMatrixField, type MatrixRates } from './convertQuoteToMatrixField'

type ItemQuote = NonNullable<RequestItem['results']>[number]

/** Допуск сверки цены квоты с ценой в ячейке — тот же, что на сервере и в «Выборе квот». */
const CELL_PRICE_TOLERANCE = 0.02

/**
 * Квота, чья цена лежит в ячейке матрицы (№73). В ячейке не обязательно цена
 * самой новой квоты: там последняя внесённая/исправленная из таблицы
 * (useMatrixCellQuotes) или автовыбранная сервером. Цену квоты приводим к
 * валюте ячейки той же конвертацией, что её пишет. Нет цен у квот (карточка
 * заявки их не отдаёт) или курсов — совпадения нет.
 */
function findQuoteByCellPrice(
  quotes: ItemQuote[],
  sourceType: string,
  cellValue: number,
  rates: MatrixRates | null | undefined,
): ItemQuote | undefined {
  if (!rates) return undefined
  return quotes.find(
    (q) =>
      q.price != null &&
      q.price > 0 &&
      Math.abs(
        convertQuoteToMatrixField(q.price, q.currency, sourceType as SourceType, rates) - cellValue,
      ) < CELL_PRICE_TOLERANCE,
  )
}

/**
 * Поставщик строки КП для выбранной ячейки матрицы (№73) — одно правило для
 * клика по ячейке, «По лучшим ценам» и «Выбрать все позиции»: квота матрицы,
 * чья цена лежит в ячейке; совпадения нет (у квот нет цен, курсы с тех пор
 * поменялись) — первая квота матрицы по порядку `results` (самая новая); квот
 * по матрице нет (цену ввёл менеджер вручную) — название матрицы.
 */
export function resolveKpSupplier(
  results: RequestItem['results'],
  matrix: string,
  cellValue: number,
  quoteRates?: MatrixRates | null,
): string | null {
  const srcType = SOURCE_TYPE_BY_MATRIX[matrix]
  let supplierName: string | null = null
  if (srcType && results?.length) {
    const sameSource = results.filter((r) => r.sourceType === srcType)
    const quote = findQuoteByCellPrice(sameSource, srcType, cellValue, quoteRates) ?? sameSource[0]
    supplierName = quote?.supplierName ?? null
  }
  return supplierName || MATRIX_LABEL_BY_KEY[matrix] || null
}

/**
 * Для каждой позиции вычисляет минимальную себестоимость в рублях по всем
 * имеющимся ценам (Склад / РФ / Производители / Стоки / Китай) и возвращает
 * её в формате SelectedPrice — так же, как получилось бы при клике на
 * лучшую ячейку в режиме КП. Используется кнопкой «По лучшим ценам» в
 * KpModePanel, чтобы менеджер мог одним нажатием заполнить все позиции
 * минимальными ценами, а потом при желании скорректировать вручную.
 *
 * Если у позиции нет ни одной цены — возвращает null (эта позиция не
 * попадёт в итоговый Map выбранных цен).
 *
 * Курс CNY и коэффициенты наценки обязательны: подставлять их «по умолчанию»
 * нельзя — вызывать функцию до загрузки справочников не разрешает гейт
 * готовности цен (см. useRequestRelatedData.pricing).
 *
 * `quoteRates` (USD/EUR/CNY) нужны только для подписи строки поставщиком:
 * сверить цену квоты с ценой в ячейке. Без них (или без цен у квот) поставщик —
 * первая квота выигравшей матрицы в порядке `results`, как было раньше.
 */
export function computeBestPriceForItem(
  item: RequestItem,
  cnyRate: number,
  markupConstants: MarkupConstants,
  quoteRates?: MatrixRates | null,
): SelectedPrice | null {
  const toRub = (raw: number | null | undefined, matrix: string): number | null => {
    // Цена 0 (или меньше) не считается реальной ценой — позиция с нулём во
    // всех матрицах эквивалентна позиции вообще без цен. Лучшая цена > 0.
    if (raw == null || raw <= 0) return null
    const markupKey = MARKUP_KEY_BY_MATRIX[matrix]
    const k = markupKey ? markupConstants[markupKey] : undefined
    // Коэффициента матрицы нет в справочнике — матрица не становится
    // кандидатом. Зашитого «среднего» коэффициента больше нет.
    if (k == null) return null
    if (matrix === 'rf') return raw * k
    return raw * k * cnyRate
  }

  // raw — значение ячейки в её валюте (₽ для РФ, ¥ для остальных): по нему
  // ищется квота-источник цены.
  type Candidate = { col: string; matrix: string; cost: number; raw: number }
  const candidates: Candidate[] = []

  // Порядок важен: при равной стоимости выигрывает первый кандидат
  // (Склад → Производители → Стоки → РФ → Китай).
  const addCandidate = (col: string, matrix: string, raw: number | null | undefined) => {
    const cost = toRub(raw, matrix)
    if (cost != null) candidates.push({ col, matrix, cost, raw: Number(raw) })
  }
  addCandidate('priceStock', 'stock', item.priceStock)
  addCandidate('priceManufacturer', 'manuf', item.priceManufacturer)
  addCandidate('priceOther', 'other', item.priceOther)
  addCandidate('priceRF', 'rf', item.priceRF)
  // Китай хранится в поле targetPrice, но в SelectedPrice это col='price'.
  addCandidate('price', 'china', item.targetPrice)

  if (candidates.length === 0) return null

  // Минимальная цена среди всех доступных матриц.
  const best = candidates.reduce((min, c) => (c.cost < min.cost ? c : min))

  return {
    col: best.col,
    matrix: best.matrix,
    cost: best.cost,
    // Реальный поставщик выигравшей матрицы — квота, чья цена лежит в ячейке.
    supplierName: resolveKpSupplier(item.results, best.matrix, best.raw, quoteRates),
  }
}
